import { faBars, faBookOpen, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useUrlQueryParam } from "./routing";
import { colors } from "./constants";

// The three pieces of app chrome that can be dismissed independently, each
// backed by a URL param so a hidden bar survives a refresh and steps back with
// browser history. An absent param means visible, so a plain URL shows everything.
export type ChromePart = 'topBar' | 'search' | 'rail';

const CHROME_PARAMS: Record<ChromePart, string> = {
  topBar: 'hideTopBar',
  search: 'hideSearch',
  rail: 'hideRail',
};

export function useChromePart(part: ChromePart): [boolean, (hidden: boolean) => void] {
  const [raw, setRaw] = useUrlQueryParam(CHROME_PARAMS[part], '');
  return [raw === '1', (hidden: boolean) => setRaw(hidden ? '1' : '')];
}

// A small dismiss affordance - it sits inside chrome that's already dense, so it
// takes its color from the caller rather than introducing another button style.
export function HideChromeButton({
  onHide,
  label,
  size = 20,
  style = {},
}: {
  onHide: () => void;
  label: string;
  size?: number;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onHide}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size}px`,
        height: `${size}px`,
        flex: `0 0 ${size}px`,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        fontSize: `${Math.round(size * 0.55)}px`,
        lineHeight: 1,
        ...style,
      }}
    >
      <FontAwesomeIcon icon={faXmark} />
    </button>
  );
}

// One restore icon per hidden part, in a fixed order so the buttons don't jump
// around as parts are hidden and shown. Renders nothing while everything is
// visible, so the top bar stays uncluttered in the normal case.
const RESTORABLE: { part: ChromePart; icon: any; label: string }[] = [
  { part: 'topBar', icon: faBars, label: 'Show top bar' },
  { part: 'rail', icon: faBookOpen, label: 'Show navigation rail' },
  { part: 'search', icon: faMagnifyingGlass, label: 'Show search sidebar' },
];

export function RestoreChromeButtons({ size = 32 }: { size?: number }) {
  // Called unconditionally and in a fixed order - one hook per restorable part.
  const [topBarHidden, setTopBarHidden] = useChromePart('topBar');
  const [railHidden, setRailHidden] = useChromePart('rail');
  const [searchHidden, setSearchHidden] = useChromePart('search');

  const state: Record<ChromePart, [boolean, (hidden: boolean) => void]> = {
    topBar: [topBarHidden, setTopBarHidden],
    rail: [railHidden, setRailHidden],
    search: [searchHidden, setSearchHidden],
  };

  const restorable = RESTORABLE
    .map((entry) => ({ ...entry, setHidden: state[entry.part][1] }))
    .filter((entry) => state[entry.part][0]);
  if (!restorable.length) { return null }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '4px',
    }}>
      {restorable.map((p) => (
        <button
          key={p.part}
          type="button"
          aria-label={p.label}
          title={p.label}
          onClick={() => p.setHidden(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: `${size}px`,
            height: `${size}px`,
            padding: 0,
            cursor: 'pointer',
            fontSize: '15px',
            border: '1px solid',
            backgroundColor: colors['buttonBackground'],
            borderColor: colors['buttonBorder'],
            color: colors['buttonText'],
          }}
        >
          <FontAwesomeIcon icon={p.icon} />
        </button>
      ))}
    </div>
  );
}
